/* eslint-disable @typescript-eslint/no-explicit-any */
import type { DeliverySettings, FulfillmentConfiguration, PickupSettings } from '@squareup/shared-types-protos/squareup/channelcontrols/configuration/models.js';
import { parse } from '@squareup/util-duration';

export const MAX_PREP_TIME_MINUTES = 120;

const MINS_IN_HOUR = 60;
const MINS_IN_DAY = 1440;
const MINS_IN_WEEK = 10080;

type TimeUnit = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks';

const timeToMinutesMap: Record<TimeUnit, number> = {
  seconds: 0,
  minutes: 1,
  hours: MINS_IN_HOUR,
  days: MINS_IN_DAY,
  weeks: MINS_IN_WEEK,
};

/**
 * Parses a prep time string into number of minutes
 * @param prepTimeString - String format: 'PT{number}{unit}', ('PT20M') or multiple units ('PT1H30M')
 * @returns Int value to represent minutes
 * @example parsePrepTimeString('PT1H30M') returns 90
 */
export function parsePrepTimeString(prepTimeString: string): number {
  if (!prepTimeString.length) {
    return 0;
  }

  let totalMinutes = 0;
  const parsed = parse(prepTimeString);

  (Object.keys(parsed) as TimeUnit[]).forEach((key) => {
    const value = parsed[key] || 0;
    const multiplier = timeToMinutesMap[key];
    const minutesToAdd = value * multiplier;
    totalMinutes += minutesToAdd;
  });

  return totalMinutes;
}

/**
 * @param value - int value to represent minutes
 * @param t - Translation function from react-i18next
 * @returns Translated prep time text or fallback for no prep time
 */
export function formatPrepTimeText(
  value: number | null,
  t: (key: string, options?: { count?: number }) => string,
) {
  if (!value) {
    return t('cashApp.prepTime.noPrep');
  }
  return `${t('cashApp.prepTime.modal.label')}: ${t('cashApp.prepTime.minutes', { count: value })}`;
}

/**
 * Filters fulfillment configurations to only include locations with the specified settings key
 * @param fulfillmentConfigurations - Object with location IDs as keys and fulfillment configs as values
 * @param settingsKey - Settings key ('pickupSettings' or 'deliverySettings')
 * @returns Object with only locations that have the specified settings configured
 */
export function getFulfillmentConfigurationsByType(
  fulfillmentConfigurations: Record<string, any>,
  settingsKey: 'pickupSettings' | 'deliverySettings',
) {
  const fulfillmentConfigurationsByType: Record<string, any> = {};

  Object.entries(fulfillmentConfigurations).forEach(([locationId, config]) => {
    const settings = config?.[settingsKey];

    // Include location if it has settings for this key
    if (settings) {
      fulfillmentConfigurationsByType[locationId] = config;
    }
  });

  return fulfillmentConfigurationsByType;
}

export function getPrepTimeNumberForSingleConfig(fulfillmentConfigurationByType?: PickupSettings | DeliverySettings) {
  const prepTimeDuration = fulfillmentConfigurationByType?.prepTimeDuration;
  if (prepTimeDuration && typeof prepTimeDuration === 'string') {
    return parsePrepTimeString(prepTimeDuration);
  }
  return null;
}

/**
 * Takes a map fulfillment configurations and returns a Map of strings by location id
 * @param fulfillmentConfigurations - Object with location IDs as keys and fulfillment configs as values
 * @returns Object with location IDs as keys and translated prep time text as values
 */
export function getPrepTimeStrings(
  fulfillmentConfigurations: Record<string, FulfillmentConfiguration>,
  settingsKey: 'pickupSettings' | 'deliverySettings',
  t: (key: string, options?: { count?: number }) => string,
) {
  const prepTimeTexts: Record<string, string> = {};

  Object.entries(fulfillmentConfigurations).forEach(([locationId, config]) => {
    const settings = config?.[settingsKey];
    const prepTimeDuration = settings?.prepTimeDuration;

    let prepTime = null;
    if (prepTimeDuration && typeof prepTimeDuration === 'string') {
      prepTime = parsePrepTimeString(prepTimeDuration);
    }

    prepTimeTexts[locationId] = formatPrepTimeText(prepTime, t);
  });

  return prepTimeTexts;
}
